/**
 * 🎮 RamboArcade — Cena jogável (4ª no ciclo).
 *
 * Controles:
 *  - ← → ou A D: mover
 *  - ↑ ou W: pular
 *  - ESPAÇO: atirar
 *  - ESC: sair
 *
 * Objetivo: abater 15 inimigos antes de perder 3 vidas.
 * Ao vencer: reveal do logo ESCALONE.
 */
import { useEffect, useRef, useState } from 'react';

const LW = 480;
const LH = 270;

const C = {
  skyTop: '#0a0f24',
  skyMid: '#2a2050',
  skyHorizon: '#6a3040',
  mountain: '#1a1830',
  ground: '#1a1a18',
  groundTop: '#2e2a20',
  grass: '#4a6a2a',
  bandana: '#b42820',
  skin: '#d89866',
  skinDark: '#a06040',
  pants: '#1e1e24',
  boot: '#0a0a0a',
  gun: '#18181c',
  gunHi: '#5a5a64',
  flash: '#fff2a0',
  bullet: '#fff4b0',
  enemy: '#6a2818',
  enemyDark: '#3a1408',
  enemyEye: '#ff4040',
  hp: '#c82020',
  hpBg: '#3a1010',
  logoA: '#3b82f6',
  logoB: '#8b5cf6',
};

type Bullet = { x: number; y: number; vx: number; alive: boolean; fromPlayer: boolean };
type Enemy = { x: number; y: number; vx: number; hp: number; alive: boolean; dir: 1 | -1; shootCD: number };

export default function RamboArcade({ onFinish }: { onFinish: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [status, setStatus] = useState<'intro' | 'playing' | 'won' | 'lost'>('intro');
  const statusRef = useRef(status);
  statusRef.current = status;

  const end = () => {
    setFinishing(true);
    setTimeout(() => onFinish(), 450);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = '100vw';
      canvas.style.height = '100vh';
    };
    resize();
    window.addEventListener('resize', resize);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const offscreen = document.createElement('canvas');
    offscreen.width = LW; offscreen.height = LH;
    const octx = offscreen.getContext('2d');
    if (!octx) return;
    octx.imageSmoothingEnabled = false;
    ctx.imageSmoothingEnabled = false;

    const pr = (x: number, y: number, w: number, h: number, color: string) => {
      octx.fillStyle = color;
      octx.fillRect(Math.round(x), Math.round(y), Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
    };

    // ----- Audio -----
    let ac: AudioContext | null = null;
    try { ac = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)(); } catch { /* ignore */ }
    const anow = () => (ac ? ac.currentTime : 0);
    const playShot = () => {
      if (!ac) return;
      const buf = ac.createBuffer(1, ac.sampleRate * 0.1, ac.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ac.sampleRate * 0.015));
      const src = ac.createBufferSource(); src.buffer = buf;
      const f = ac.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = 1200;
      const g = ac.createGain(); g.gain.value = 0.35;
      src.connect(f); f.connect(g); g.connect(ac.destination); src.start();
    };
    const playHit = () => {
      if (!ac) return;
      const t0 = anow();
      const o = ac.createOscillator(); o.type = 'square'; o.frequency.value = 220;
      o.frequency.exponentialRampToValueAtTime(60, t0 + 0.15);
      const g = ac.createGain();
      g.gain.setValueAtTime(0.22, t0);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.2);
      o.connect(g); g.connect(ac.destination);
      o.start(t0); o.stop(t0 + 0.22);
    };
    const playHurt = () => {
      if (!ac) return;
      const t0 = anow();
      const o = ac.createOscillator(); o.type = 'sawtooth'; o.frequency.value = 140;
      o.frequency.exponentialRampToValueAtTime(40, t0 + 0.3);
      const g = ac.createGain();
      g.gain.setValueAtTime(0.28, t0);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      o.connect(g); g.connect(ac.destination);
      o.start(t0); o.stop(t0 + 0.4);
    };
    const playWin = () => {
      if (!ac) return;
      const t0 = anow();
      [261.63, 329.63, 392.00, 523.25, 659.25].forEach((f, i) => {
        const o = ac!.createOscillator(); o.type = 'triangle'; o.frequency.value = f;
        const g = ac!.createGain();
        g.gain.setValueAtTime(0.0001, t0 + i * 0.12);
        g.gain.linearRampToValueAtTime(0.18, t0 + i * 0.12 + 0.05);
        g.gain.linearRampToValueAtTime(0.0001, t0 + i * 0.12 + 0.8);
        o.connect(g); g.connect(ac!.destination);
        o.start(t0 + i * 0.12); o.stop(t0 + i * 0.12 + 0.9);
      });
    };
    const playLose = () => {
      if (!ac) return;
      const t0 = anow();
      [220, 185, 155, 130].forEach((f, i) => {
        const o = ac!.createOscillator(); o.type = 'square'; o.frequency.value = f;
        const g = ac!.createGain();
        g.gain.setValueAtTime(0.0001, t0 + i * 0.2);
        g.gain.linearRampToValueAtTime(0.2, t0 + i * 0.2 + 0.05);
        g.gain.linearRampToValueAtTime(0.0001, t0 + i * 0.2 + 0.6);
        o.connect(g); g.connect(ac!.destination);
        o.start(t0 + i * 0.2); o.stop(t0 + i * 0.2 + 0.65);
      });
    };

    // ----- State -----
    const GROUND_Y = LH - 52;
    const keys = { left: false, right: false, shoot: false };
    const player = { x: 40, y: GROUND_Y, w: 18, h: 28, dir: 1 as 1 | -1, flashT: 0, shootCD: 0, hp: 3, hurtT: 0, walkPhase: 0, vy: 0, onGround: true };
    const bullets: Bullet[] = [];
    const enemies: Enemy[] = [];
    let killed = 0;
    const KILL_TARGET = 15;
    let spawnCD = 800;
    let gameStartAt = 0;
    let endAt = 0;
    let shake = 0;
    let lastFrame = performance.now();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { end(); return; }
      if (statusRef.current === 'intro' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        setStatus('playing');
        gameStartAt = performance.now();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') keys.left = true;
      if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') keys.right = true;
      if (e.key === 'ArrowUp' || e.key.toLowerCase() === 'w') {
        if (player.onGround && statusRef.current === 'playing') {
          player.vy = -4.2;
          player.onGround = false;
        }
        e.preventDefault();
      }
      if (e.key === ' ') { keys.shoot = true; e.preventDefault(); }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') keys.left = false;
      if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') keys.right = false;
      if (e.key === ' ') keys.shoot = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    // ----- Sprites -----
    const drawPlayer = () => {
      const sx = player.x, sy = player.y;
      const walk = Math.floor(player.walkPhase) % 4;
      const bob = (walk === 1 || walk === 3) ? -1 : 0;
      // Piscar se ferido
      const hurt = player.hurtT > 0 && Math.floor(player.hurtT / 4) % 2 === 0;
      if (hurt) { octx.globalAlpha = 0.4; }

      // Sombra
      pr(sx, sy + 28, 18, 1, 'rgba(0,0,0,0.5)');
      // Pernas (animação)
      if (walk === 0) { pr(sx + 5, sy + 22, 3, 6, C.pants); pr(sx + 10, sy + 22, 3, 6, C.pants); }
      else if (walk === 1) { pr(sx + 4, sy + 22, 3, 5, C.pants); pr(sx + 11, sy + 22, 3, 6, C.pants); }
      else if (walk === 2) { pr(sx + 5, sy + 22, 3, 6, C.pants); pr(sx + 10, sy + 22, 3, 6, C.pants); }
      else { pr(sx + 5, sy + 22, 3, 6, C.pants); pr(sx + 12, sy + 22, 3, 5, C.pants); }
      // Botas
      pr(sx + 4, sy + 27, 4, 1, C.boot);
      pr(sx + 10, sy + 27, 4, 1, C.boot);
      // Calça cinto
      pr(sx + 4, sy + 21, 11, 1, '#1a1208');
      // Torso nu musculoso
      pr(sx + 3 + bob, sy + 10 + bob, 12, 12, C.skin);
      pr(sx + 3 + bob, sy + 10 + bob, 12, 1, C.skinDark);
      pr(sx + 3 + bob, sy + 21 + bob, 12, 1, C.skinDark);
      pr(sx + 8 + bob, sy + 11 + bob, 1, 9, C.skinDark); // linha peito
      pr(sx + 5 + bob, sy + 16 + bob, 4, 1, C.skinDark);
      pr(sx + 9 + bob, sy + 16 + bob, 4, 1, C.skinDark);
      pr(sx + 6 + bob, sy + 18 + bob, 6, 1, C.skinDark);
      // Cinta munição
      for (let i = 0; i < 8; i++) pr(sx + 3 + i + bob, sy + 11 + i + bob, 1, 1, '#3a2818');
      for (let i = 0; i < 5; i++) pr(sx + 4 + i * 2 + bob, sy + 12 + i * 2 + bob, 1, 1, '#f0c050');
      // Braços
      pr(sx + 1 + bob, sy + 10 + bob, 3, 7, C.skin);
      pr(sx + 14 + bob, sy + 10 + bob, 3, 7, C.skin);
      // Cabeça
      pr(sx + 5 + bob, sy + 3 + bob, 8, 7, C.skin);
      pr(sx + 5 + bob, sy + 9 + bob, 8, 1, C.skinDark);
      // Bandana
      pr(sx + 5 + bob, sy + 2 + bob, 8, 2, C.bandana);
      pr(sx + 3 + bob, sy + 4 + bob, 2, 1, C.bandana);
      pr(sx + 2 + bob, sy + 5 + bob, 2, 1, C.bandana);
      // Olhos
      pr(sx + 7 + bob, sy + 6 + bob, 1, 1, '#000');
      pr(sx + 10 + bob, sy + 6 + bob, 1, 1, '#000');
      // Sobrancelha
      pr(sx + 6 + bob, sy + 5 + bob, 2, 1, C.skinDark);
      pr(sx + 10 + bob, sy + 5 + bob, 2, 1, C.skinDark);
      // Boca
      pr(sx + 8 + bob, sy + 8 + bob, 3, 1, C.skinDark);
      // M60 na direção que olha
      const d = player.dir;
      const gunY = sy + 14 + bob;
      if (d > 0) {
        pr(sx + 16, gunY, 10, 3, C.gun);
        pr(sx + 16, gunY, 10, 1, C.gunHi);
        pr(sx + 25, gunY + 1, 5, 1, C.gun);
      } else {
        pr(sx - 8, gunY, 10, 3, C.gun);
        pr(sx - 8, gunY, 10, 1, C.gunHi);
        pr(sx - 12, gunY + 1, 4, 1, C.gun);
      }
      // Flash
      if (player.flashT > 0) {
        const fx = d > 0 ? sx + 30 : sx - 12;
        const fy = gunY + 1;
        const rg = octx.createRadialGradient(fx, fy, 0, fx, fy, 14);
        rg.addColorStop(0, 'rgba(255,240,160,1)');
        rg.addColorStop(1, 'rgba(255,100,30,0)');
        octx.fillStyle = rg; octx.fillRect(fx - 14, fy - 14, 28, 28);
      }
      if (hurt) octx.globalAlpha = 1;
    };

    const drawEnemy = (e: Enemy) => {
      const x = e.x, y = e.y;
      pr(x - 1, y + 22, 10, 1, 'rgba(0,0,0,0.5)');
      // Corpo vermelho
      pr(x, y + 8, 8, 10, C.enemy);
      pr(x, y + 17, 8, 1, C.enemyDark);
      // Pernas
      pr(x + 1, y + 18, 2, 4, C.enemyDark);
      pr(x + 5, y + 18, 2, 4, C.enemyDark);
      // Cabeça capuz
      pr(x + 1, y, 6, 7, C.enemyDark);
      pr(x + 1, y + 6, 6, 1, '#0a0a0a');
      // Olhos vermelhos
      pr(x + 2, y + 3, 1, 1, C.enemyEye);
      pr(x + 5, y + 3, 1, 1, C.enemyEye);
      // Arma
      if (e.dir > 0) {
        pr(x + 8, y + 11, 5, 2, C.gun);
      } else {
        pr(x - 5, y + 11, 5, 2, C.gun);
      }
      // Barra de HP acima
      if (e.hp < 3) {
        pr(x - 1, y - 3, 10, 2, C.hpBg);
        pr(x, y - 2, Math.floor(8 * (e.hp / 3)), 1, C.hp);
      }
    };

    // ----- Partículas / poeira -----
    type P = { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number };
    const sparks: P[] = [];
    const spawnSparks = (x: number, y: number, n: number, color = C.flash) => {
      for (let i = 0; i < n; i++) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 1 + Math.random() * 3;
        sparks.push({
          x, y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp - 0.5,
          life: 18, max: 18,
          color,
          size: 1 + Math.floor(Math.random() * 2),
        });
      }
    };

    // ----- Logo -----
    const logoP: Array<{ x: number; y: number; tx: number; ty: number; color: string }> = [];
    let logoSpawned = false;
    const spawnLogo = () => {
      const mctx = document.createElement('canvas');
      mctx.width = LW; mctx.height = LH;
      const mc = mctx.getContext('2d');
      if (!mc) return;
      mc.imageSmoothingEnabled = false;
      mc.fillStyle = '#fff';
      mc.font = 'bold 56px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
      mc.textAlign = 'center'; mc.textBaseline = 'middle';
      mc.fillText('ESCALONE', LW / 2, LH / 2 - 8);
      const img = mc.getImageData(0, 0, LW, LH).data;
      for (let y = 0; y < LH; y += 2) {
        for (let x = 0; x < LW; x += 2) {
          if (img[(y * LW + x) * 4 + 3] > 128) {
            logoP.push({
              x: Math.random() * LW,
              y: Math.random() * LH,
              tx: x, ty: y,
              color: Math.random() < 0.5 ? C.logoA : C.logoB,
            });
          }
        }
      }
    };

    // ----- Loop -----
    const loop = (nowMs: number) => {
      const dtReal = Math.min(50, nowMs - lastFrame);
      lastFrame = nowMs;
      const dt = dtReal / 16.67;
      const s = statusRef.current;

      octx.clearRect(0, 0, LW, LH);

      // BG
      const grad = octx.createLinearGradient(0, 0, 0, LH);
      grad.addColorStop(0, C.skyTop);
      grad.addColorStop(0.6, C.skyMid);
      grad.addColorStop(1, C.skyHorizon);
      octx.fillStyle = grad; octx.fillRect(0, 0, LW, LH);
      // Lua
      for (let dy = -8; dy <= 8; dy++) for (let dx = -8; dx <= 8; dx++)
        if (dx * dx + dy * dy <= 64) pr(LW - 60 + dx, 50 + dy, 1, 1, '#e8e8d0');
      // Estrelas
      for (let i = 0; i < 30; i++) {
        const sx = (i * 53) % LW;
        const sy = (i * 29) % 120;
        const tw = 0.5 + 0.5 * Math.sin(nowMs / 200 + i);
        if (tw > 0.7) pr(sx, sy, 1, 1, '#fff');
      }
      // Montanhas
      for (let x = 0; x < LW; x++) {
        const h = 40 + Math.sin(x / 40) * 12 + Math.sin(x / 15) * 3;
        pr(x, LH - 60 - h, 1, 70 + h, C.mountain);
      }
      // Chão
      pr(0, LH - 18, LW, 18, C.ground);
      pr(0, LH - 18, LW, 2, C.groundTop);
      // Tufos
      for (let i = 0; i < 20; i++) {
        const gx = (i * 37) % LW;
        pr(gx, LH - 16 + ((i * 7) % 4), 1, 2, C.grass);
      }

      if (s === 'playing') {
        // Atualizar player
        if (keys.left) { player.x -= 1.6 * dt; player.dir = -1; if (player.onGround) player.walkPhase += 0.3 * dt; }
        if (keys.right) { player.x += 1.6 * dt; player.dir = 1; if (player.onGround) player.walkPhase += 0.3 * dt; }
        if ((!keys.left && !keys.right) || !player.onGround) player.walkPhase = 0;
        player.x = Math.max(2, Math.min(LW - 20, player.x));
        // Gravidade / pulo
        if (!player.onGround) {
          player.vy += 0.22 * dt;
          player.y += player.vy * dt;
          if (player.y >= GROUND_Y) {
            player.y = GROUND_Y;
            player.vy = 0;
            player.onGround = true;
          }
        }

        // Atirar
        if (player.shootCD > 0) player.shootCD -= dt;
        if (player.flashT > 0) player.flashT -= dt;
        if (player.hurtT > 0) player.hurtT -= dt;
        if (keys.shoot && player.shootCD <= 0) {
          player.shootCD = 10;
          player.flashT = 5;
          shake = Math.max(shake, 3);
          const bx = player.dir > 0 ? player.x + 30 : player.x - 10;
          bullets.push({ x: bx, y: player.y + 15, vx: player.dir * 8, alive: true, fromPlayer: true });
          playShot();
        }

        // Spawn inimigos
        spawnCD -= dtReal;
        if (spawnCD <= 0 && enemies.filter(e => e.alive).length < 4) {
          spawnCD = 800 + Math.random() * 800;
          const fromRight = Math.random() < 0.5;
          enemies.push({
            x: fromRight ? LW + 10 : -10,
            y: LH - 40,
            vx: fromRight ? -0.6 - Math.random() * 0.5 : 0.6 + Math.random() * 0.5,
            hp: 3,
            alive: true,
            dir: fromRight ? -1 : 1,
            shootCD: 800 + Math.random() * 800,
          });
        }

        // Atualizar inimigos
        for (const e of enemies) {
          if (!e.alive) continue;
          e.x += e.vx * dt;
          // Parar em range e atirar
          const dist = e.x - player.x;
          if (Math.abs(dist) < 120) e.vx *= 0.92;
          e.shootCD -= dtReal;
          if (e.shootCD <= 0 && Math.abs(dist) < 200) {
            e.shootCD = 1400 + Math.random() * 800;
            bullets.push({ x: e.x + (e.dir > 0 ? 10 : -2), y: e.y + 12, vx: e.dir * 4, alive: true, fromPlayer: false });
          }
          if (e.x < -20 || e.x > LW + 20) e.alive = false;
        }

        // Atualizar balas
        for (const b of bullets) {
          if (!b.alive) continue;
          b.x += b.vx * dt;
          if (b.x < -10 || b.x > LW + 10) b.alive = false;
          if (b.fromPlayer) {
            // colisão com inimigos
            for (const e of enemies) {
              if (!e.alive) continue;
              if (b.x >= e.x && b.x <= e.x + 8 && b.y >= e.y && b.y <= e.y + 22) {
                e.hp -= 1;
                b.alive = false;
                spawnSparks(b.x, b.y, 6);
                playHit();
                if (e.hp <= 0) {
                  e.alive = false;
                  killed += 1;
                  spawnSparks(e.x + 4, e.y + 10, 20, '#c82020');
                  if (killed >= KILL_TARGET) {
                    setStatus('won');
                    endAt = performance.now();
                    playWin();
                  }
                }
                break;
              }
            }
          } else {
            // colisão com player
            if (b.x >= player.x + 2 && b.x <= player.x + 16 && b.y >= player.y + 4 && b.y <= player.y + 24) {
              b.alive = false;
              if (player.hurtT <= 0) {
                player.hp -= 1;
                player.hurtT = 45;
                shake = 8;
                spawnSparks(b.x, b.y, 10, C.bandana);
                playHurt();
                if (player.hp <= 0) {
                  setStatus('lost');
                  endAt = performance.now();
                  playLose();
                }
              }
            }
          }
        }
        // limpar
        for (let i = bullets.length - 1; i >= 0; i--) if (!bullets[i].alive) bullets.splice(i, 1);
      }

      // Desenhar balas
      for (const b of bullets) {
        if (!b.alive) continue;
        pr(b.x, b.y, 4, 1, b.fromPlayer ? C.bullet : '#ff6030');
        pr(b.x - 2, b.y, 2, 1, b.fromPlayer ? 'rgba(255,220,100,0.5)' : 'rgba(255,100,50,0.5)');
      }
      // Inimigos
      for (const e of enemies) if (e.alive) drawEnemy(e);
      // Player
      drawPlayer();

      // Partículas
      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 0.12 * dt;
        p.life -= dt;
        if (p.life <= 0) { sparks.splice(i, 1); continue; }
        octx.globalAlpha = Math.max(0, p.life / p.max);
        octx.fillStyle = p.color;
        octx.fillRect(Math.round(p.x), Math.round(p.y), p.size, p.size);
      }
      octx.globalAlpha = 1;

      // HUD
      // Vidas
      for (let i = 0; i < 3; i++) {
        octx.fillStyle = i < player.hp ? C.hp : '#3a1010';
        // coração pixelado 7x6
        const hx = 10 + i * 12, hy = 24;
        pr(hx + 1, hy, 2, 1, octx.fillStyle as string);
        pr(hx + 4, hy, 2, 1, octx.fillStyle as string);
        pr(hx, hy + 1, 7, 2, octx.fillStyle as string);
        pr(hx + 1, hy + 3, 5, 1, octx.fillStyle as string);
        pr(hx + 2, hy + 4, 3, 1, octx.fillStyle as string);
        pr(hx + 3, hy + 5, 1, 1, octx.fillStyle as string);
      }
      // Placar
      octx.fillStyle = '#fff';
      octx.font = 'bold 11px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
      octx.textAlign = 'left'; octx.textBaseline = 'top';
      octx.fillText(`ALVOS: ${killed}/${KILL_TARGET}`, LW - 110, 24);

      // Intro
      if (s === 'intro') {
        octx.fillStyle = 'rgba(0,0,0,0.7)';
        octx.fillRect(0, LH / 2 - 60, LW, 120);
        octx.fillStyle = '#f4c15a';
        octx.font = 'bold 22px "Impact", "Arial Black", system-ui, sans-serif';
        octx.textAlign = 'center'; octx.textBaseline = 'middle';
        octx.fillText('RAMBO ARCADE', LW / 2, LH / 2 - 30);
        octx.fillStyle = '#fff';
        octx.font = '600 11px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
        octx.fillText('← → mover   ↑ pular   ESPAÇO atirar', LW / 2, LH / 2 - 5);
        octx.fillText(`abata ${KILL_TARGET} inimigos`, LW / 2, LH / 2 + 15);
        octx.fillStyle = '#c82020';
        octx.font = 'bold 12px "Impact", system-ui, sans-serif';
        const blink = Math.floor(nowMs / 400) % 2 === 0;
        if (blink) octx.fillText('APERTE ESPAÇO OU ENTER', LW / 2, LH / 2 + 38);
      }

      // Won
      if (s === 'won') {
        const sinceEnd = nowMs - endAt;
        if (sinceEnd > 600 && !logoSpawned) { logoSpawned = true; spawnLogo(); }
        if (logoSpawned) {
          for (const p of logoP) {
            const dx = p.tx - p.x; const dy = p.ty - p.y;
            p.x += dx * 0.08 * dt; p.y += dy * 0.08 * dt;
            octx.fillStyle = p.color;
            octx.fillRect(Math.round(p.x), Math.round(p.y), 2, 2);
          }
          if (sinceEnd > 1500) {
            const a = Math.min(1, (sinceEnd - 1500) / 400);
            octx.save();
            octx.globalAlpha = a;
            const g = octx.createLinearGradient(0, LH / 2 - 30, LW, LH / 2 + 30);
            g.addColorStop(0, C.logoA); g.addColorStop(1, C.logoB);
            octx.fillStyle = g;
            octx.font = 'bold 56px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
            octx.textAlign = 'center'; octx.textBaseline = 'middle';
            octx.fillText('ESCALONE', LW / 2, LH / 2 - 8);
            octx.fillStyle = '#fff';
            octx.font = '600 13px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
            octx.fillText('você escalou a produtividade.', LW / 2, LH / 2 + 32);
            octx.restore();
          }
        }
        if (sinceEnd > 4500) { end(); return; }
      }
      if (s === 'lost') {
        const sinceEnd = nowMs - endAt;
        octx.fillStyle = `rgba(0,0,0,${Math.min(0.75, sinceEnd / 800)})`;
        octx.fillRect(0, 0, LW, LH);
        if (sinceEnd > 400) {
          octx.fillStyle = '#c82020';
          octx.font = 'bold 32px "Impact", "Arial Black", system-ui, sans-serif';
          octx.textAlign = 'center'; octx.textBaseline = 'middle';
          octx.fillText('GAME OVER', LW / 2, LH / 2 - 10);
          octx.fillStyle = '#fff';
          octx.font = '600 12px system-ui, sans-serif';
          octx.fillText('a planilha venceu hoje...', LW / 2, LH / 2 + 20);
        }
        if (sinceEnd > 2500) { end(); return; }
      }

      // Post-FX: scanlines + vignette + letterbox
      octx.save();
      octx.globalAlpha = 0.12;
      octx.fillStyle = '#000';
      for (let y = 0; y < LH; y += 2) octx.fillRect(0, y, LW, 1);
      octx.restore();
      const vg = octx.createRadialGradient(LW / 2, LH / 2, LW * 0.3, LW / 2, LH / 2, LW * 0.75);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(1, 'rgba(0,0,0,0.7)');
      octx.fillStyle = vg; octx.fillRect(0, 0, LW, LH);
      octx.fillStyle = '#000';
      octx.fillRect(0, 0, LW, 18);
      octx.fillRect(0, LH - 18, LW, 18);

      shake *= 0.85;

      // Blit
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const shx = (Math.random() - 0.5) * shake;
      const shy = (Math.random() - 0.5) * shake;
      const scale = Math.min(canvas.width / LW, canvas.height / LH);
      const dw = LW * scale;
      const dh = LH * scale;
      ctx.drawImage(
        offscreen,
        Math.round((canvas.width - dw) / 2 + shx),
        Math.round((canvas.height - dh) / 2 + shy),
        Math.round(dw), Math.round(dh),
      );

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ac) { try { ac.close(); } catch { /* ignore */ } }
    };
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-black transition-opacity duration-500 ${finishing ? 'opacity-0' : 'opacity-100'}`}
      style={{ cursor: 'crosshair' }}
    >
      <canvas ref={canvasRef} className="block" />
      <button
        onClick={end}
        className="absolute top-4 right-4 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium backdrop-blur-sm border border-white/20"
        title="Pular (ESC)"
      >
        Pular »
      </button>
    </div>
  );
}
